<script lang="ts">
import type { HTMLAttributes } from 'vue'
import type { InputGroupProps } from '#ui-kit/ui/input-group'

export interface DateInputProps {
  class?: HTMLAttributes['class']
  size?: InputGroupProps['size']
  error?: InputGroupProps['error']
  disabled?: boolean
  min?: string
  max?: string
  noFutureDates?: boolean
  placeholder?: string
  align?: 'start' | 'center' | 'end'
}
</script>

<script setup lang="ts">
import type { DateValue } from '@internationalized/date'
import { getLocalTimeZone, minDate, toCalendarDate, today } from '@internationalized/date'
import { CalendarIcon } from '@heroicons/vue/24/outline'
import { DateFieldInput, DateFieldRoot } from 'reka-ui'
import { computed, shallowRef, watch } from 'vue'
import { fromIsoDate } from '#ui-kit/lib/iso-date'
import { Calendar } from '#ui-kit/ui/calendar'
import { InputGroup, InputGroupAddon, InputGroupButton } from '#ui-kit/ui/input-group'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '#ui-kit/ui/popover'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<DateInputProps>(), {
  size: 'md',
  align: 'end',
})

const slots = defineSlots<{
  trigger?: () => unknown
}>()

const modelValue = defineModel<string | null>()

const open = shallowRef(false)
const focused = shallowRef(false)
// A typed date is outside the limits; the field shows it while the model keeps the last valid day.
const rejected = shallowRef(false)
// Remounting the field makes it show the model again after a rejected or partial date is abandoned.
const fieldKey = shallowRef(0)
// Refreshed on open/focus so a page left open past midnight caps at the new day.
const now = shallowRef(today(getLocalTimeZone()))

const minValue = computed(() => fromIsoDate(props.min))
const maxValue = computed(() => {
  const max = fromIsoDate(props.max)
  return props.noFutureDates ? (minDate(max, now.value) ?? undefined) : max
})

const fieldValue = computed<DateValue | undefined>({
  get: () => fromIsoDate(modelValue.value),
  set: (value) => {
    if (!value) {
      rejected.value = false
      modelValue.value = null
      return
    }
    const min = minValue.value
    const max = maxValue.value
    if ((min && value.compare(min) < 0) || (max && value.compare(max) > 0)) {
      rejected.value = true
      return
    }
    rejected.value = false
    modelValue.value = toCalendarDate(value).toString()
  },
})

const showPlaceholder = computed(() => !!props.placeholder && !modelValue.value && !focused.value)

watch(modelValue, () => {
  rejected.value = false
})

function refreshToday(): void {
  now.value = today(getLocalTimeZone())
}

function setOpen(value: boolean): void {
  if (value) {
    refreshToday()
  }
  open.value = value
}

function onFocusIn(): void {
  focused.value = true
  refreshToday()
}

function onFocusOut(event: FocusEvent): void {
  const field = event.currentTarget
  if (field instanceof Element && event.relatedTarget instanceof Node && field.contains(event.relatedTarget)) {
    return
  }
  focused.value = false
  // Reka clears the model when any segment is emptied but keeps the rest; drop those too.
  if (rejected.value || !modelValue.value) {
    rejected.value = false
    fieldKey.value++
  }
}

function onPick(value: DateValue | DateValue[] | undefined): void {
  if (!value || Array.isArray(value)) {
    return
  }
  fieldValue.value = value
  open.value = false
}
</script>

<template>
  <Popover :open="open" @update:open="setOpen">
    <PopoverTrigger v-if="slots.trigger" as-child>
      <slot name="trigger" />
    </PopoverTrigger>
    <PopoverAnchor v-else as-child>
      <InputGroup :size="props.size" :error="props.error || rejected" :disabled="props.disabled" :class="props.class">
        <div class="relative flex flex-1 items-center" @focusin="onFocusIn" @focusout="onFocusOut">
          <span
            v-if="showPlaceholder"
            aria-hidden="true"
            class="pointer-events-none absolute inset-y-0 left-0 flex items-center text-muted-fg"
          >
            {{ props.placeholder }}
          </span>
          <DateFieldRoot
            :key="fieldKey"
            v-slot="{ segments }"
            v-bind="$attrs"
            v-model="fieldValue"
            :min-value="minValue"
            :max-value="maxValue"
            :disabled="props.disabled"
            class="flex items-center tabular-nums select-none"
            :class="{ 'opacity-0': showPlaceholder }"
          >
            <template v-for="item in segments" :key="item.part">
              <DateFieldInput v-if="item.part === 'literal'" :part="item.part">
                {{ item.value }}
              </DateFieldInput>
              <DateFieldInput
                v-else
                :part="item.part"
                :aria-invalid="props.error || rejected ? true : undefined"
                class="rounded-sm px-0.5 focus:bg-neutral-300 focus:outline-hidden data-placeholder:text-muted-fg"
              >
                {{ item.value }}
              </DateFieldInput>
            </template>
          </DateFieldRoot>
        </div>
        <InputGroupAddon>
          <PopoverTrigger as-child>
            <InputGroupButton aria-label="Open calendar">
              <CalendarIcon />
            </InputGroupButton>
          </PopoverTrigger>
        </InputGroupAddon>
      </InputGroup>
    </PopoverAnchor>
    <PopoverContent :align="props.align" class="p-4">
      <Calendar
        :model-value="fieldValue"
        :default-placeholder="fieldValue ?? maxValue ?? now"
        :min-value="minValue"
        :max-value="maxValue"
        prevent-deselect
        @update:model-value="onPick"
      />
    </PopoverContent>
  </Popover>
</template>
